import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import SearchableDropdown from './SearchableDropdown';
import { SuccessSwal, ErrorSwal } from '@helpers/swal';
import { useConductoresSearch } from '../../hooks/useConductoresSearch';
import { useSelector } from 'react-redux';

// Estilos para el modal en primer plano
const modalForegroundStyle = `
  .modal-foreground .modal-content {
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.7);
    border: 3px solid #007bff;
    background-color: #ffffff !important;
    position: relative;
    z-index: 1060;
  }
  .modal-foreground .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.1) !important;
  }
`;

// Inyectar estilos si no existen
if (!document.getElementById('modal-foreground-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'modal-foreground-styles';
  styleSheet.textContent = modalForegroundStyle;
  document.head.appendChild(styleSheet);
}

const EditUsuarioSpreadModal = ({
  show,
  onHide,
  usuarioSpread,
  conductores,
  empresas,
  tiposFlota,
  centrosDespacho,
  sistemasTMS,
  tiposDocumento,
  onAssign,
  onCreateConductor,
  loading = false
}) => {
  const user = useSelector(state => state.auth.user);
  const { conductores: conductoresBasic, loadingConductores, handleSearchChangeBasic, clearSearch } = useConductoresSearch(user);
  const [formData, setFormData] = useState({
    conductor_id: null,
    empresa_id: null,
    tipo_flota_id: null,
    centro_despacho_id: null,
    tms_id: null
  });
  const [conductorData, setConductorData] = useState({
    nombre: '',
    apellido: '',
    rut: '',
    telefono: '',
    email: '',
    tipo_documento_id: null
  });
  const [isNewConductor, setIsNewConductor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedConductor, setSelectedConductor] = useState(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [selectedTipoFlota, setSelectedTipoFlota] = useState(null);
  const [selectedCentroDespacho, setSelectedCentroDespacho] = useState(null);
  const [selectedTMS, setSelectedTMS] = useState(null);

  // Resetear formulario cuando se abre el modal
  useEffect(() => {
    if (show && usuarioSpread) {
      const conductorId = usuarioSpread.conductor?.id || null;
      setFormData({
        conductor_id: conductorId,
        empresa_id: usuarioSpread.empresa?.id || null,
        tipo_flota_id: usuarioSpread.tipo_flota?.id || null,
        centro_despacho_id: usuarioSpread.centro_despacho?.id || null,
        tms_id: usuarioSpread.tms?.id || null
      });
      setConductorData({
        nombre: '',
        apellido: '',
        rut: '',
        telefono: '',
        email: '',
        tipo_documento_id: null
      });
      setIsNewConductor(false);
      
      // Inicializar selectedConductor si hay conductor asignado
      if (conductorId && usuarioSpread.conductor) {
        setSelectedConductor({
          value: conductorId,
          label: usuarioSpread.conductor.nombre ? `${usuarioSpread.conductor.nombre} ${usuarioSpread.conductor.apellido}` : usuarioSpread.conductor.usuarioSpread || 'Conductor',
          data: usuarioSpread.conductor
        });
      } else {
        setSelectedConductor(null);
      }

      // Inicializar otros selectores
      if (usuarioSpread.empresa) {
        setSelectedEmpresa({
          value: usuarioSpread.empresa.id,
          label: usuarioSpread.empresa.sigla,
          data: usuarioSpread.empresa
        });
      } else {
        setSelectedEmpresa(null);
      }

      if (usuarioSpread.tipo_flota) {
        setSelectedTipoFlota({
          value: usuarioSpread.tipo_flota.id,
          label: usuarioSpread.tipo_flota.tipo_flota,
          data: usuarioSpread.tipo_flota
        });
      } else {
        setSelectedTipoFlota(null);
      }

      if (usuarioSpread.centro_despacho) {
        setSelectedCentroDespacho({
          value: usuarioSpread.centro_despacho.id,
          label: usuarioSpread.centro_despacho.centro_despacho,
          data: usuarioSpread.centro_despacho
        });
      } else {
        setSelectedCentroDespacho(null);
      }

      if (usuarioSpread.tms) {
        setSelectedTMS({
          value: usuarioSpread.tms.id,
          label: usuarioSpread.tms.tms,
          data: usuarioSpread.tms
        });
      } else {
        setSelectedTMS(null);
      }
      
      // Limpiar búsqueda de conductores
      clearSearch();
    }
  }, [show, usuarioSpread]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConductorChange = (selectedOption) => {
    setSelectedConductor(selectedOption);
    setFormData(prev => ({ 
      ...prev, 
      conductor_id: selectedOption?.value || null 
    }));
  };

  const handleEmpresaChange = (selectedOption) => {
    setSelectedEmpresa(selectedOption);
    setFormData(prev => ({ 
      ...prev, 
      empresa_id: selectedOption?.value || null 
    }));
  };

  const handleTipoFlotaChange = (selectedOption) => {
    setSelectedTipoFlota(selectedOption);
    setFormData(prev => ({ 
      ...prev, 
      tipo_flota_id: selectedOption?.value || null 
    }));
  };

  const handleCentroDespachoChange = (selectedOption) => {
    setSelectedCentroDespacho(selectedOption);
    setFormData(prev => ({ 
      ...prev, 
      centro_despacho_id: selectedOption?.value || null 
    }));
  };

  const handleTMSChange = (selectedOption) => {
    setSelectedTMS(selectedOption);
    setFormData(prev => ({ 
      ...prev, 
      tms_id: selectedOption?.value || null 
    }));
  };

  const handleConductorDataChange = (field, value) => {
    setConductorData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (isNewConductor) {
      // Validar datos del nuevo conductor
      if (!conductorData.nombre || !conductorData.apellido || !conductorData.rut || 
          !conductorData.telefono || !conductorData.email || !conductorData.tipo_documento_id) {
        ErrorSwal('Error', 'Todos los campos del conductor son obligatorios');
        return;
      }
      if (!formData.empresa_id) {
        ErrorSwal('Error', 'Empresa es campo obligatorio');
        return;
      }
    } else {
      // Validar conductor existente
      if (!formData.conductor_id || !formData.empresa_id) {
        ErrorSwal('Error', 'Conductor y Empresa son campos obligatorios');
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      let result;
      
      if (isNewConductor) {
        // Crear nuevo conductor
        const createData = {
          empresa_id: formData.empresa_id,
          tipo_flota_id: formData.tipo_flota_id,
          centro_despacho_id: formData.centro_despacho_id,
          tms_id: formData.tms_id,
          conductor_data: conductorData
        };
        result = await onCreateConductor(usuarioSpread.id, createData);
      } else {
        // Asignar conductor existente
        result = await onAssign(usuarioSpread.id, formData);
      }
      
      if (result.success) {
        SuccessSwal('Éxito', result.message || 'Usuario móvil actualizado exitosamente');
        onHide();
      } else {
        ErrorSwal('Error', result.error || 'Error al actualizar usuario móvil');
      }
    } catch (error) {
      ErrorSwal('Error', 'Error inesperado al actualizar usuario móvil');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Preparar opciones para conductores (ya no se usa - reemplazado por búsqueda dinámica)
  // const conductorOptions = conductores.map(conductor => ({
  //   ...conductor,
  //   fullName: `${conductor.nombre} ${conductor.apellido}`.trim()
  // }));

  // Verificar si un campo está en missing_fields
  const isMissingField = (fieldName) => {
    return usuarioSpread?.missing_fields?.includes(fieldName);
  };

  // Verificar si un campo es editable (está en missing_fields)
  const isFieldEditable = (fieldName) => {
    return isMissingField(fieldName);
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      backdrop={true}
      style={{ zIndex: 1055 }}
      className="modal-foreground"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Modificando usuario: {usuarioSpread?.usuarioSpread || 'Sin nombre'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando datos...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* Switch para conductor existente/nuevo */}
              <Col xs={12} className="mb-3">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="new-conductor-switch"
                    label={isNewConductor ? "Crear nuevo conductor" : "Seleccionar conductor existente"}
                    checked={isNewConductor}
                    onChange={(e) => setIsNewConductor(e.target.checked)}
                    disabled={!isFieldEditable('conductor')}
                  />
                </Form.Group>
              </Col>

              {/* Conductor - Campos condicionales */}
              {isNewConductor ? (
                <>
                  {/* Nombre del conductor */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        Nombre <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={conductorData.nombre}
                        onChange={(e) => handleConductorDataChange('nombre', e.target.value)}
                        placeholder="Ingrese nombre..."
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>

                  {/* Apellido del conductor */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        Apellido <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={conductorData.apellido}
                        onChange={(e) => handleConductorDataChange('apellido', e.target.value)}
                        placeholder="Ingrese apellido..."
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>

                  {/* RUT del conductor */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        RUT <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={conductorData.rut}
                        onChange={(e) => handleConductorDataChange('rut', e.target.value)}
                        placeholder="Ingrese RUT..."
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>

                  {/* Teléfono del conductor */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        Teléfono <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={conductorData.telefono}
                        onChange={(e) => handleConductorDataChange('telefono', e.target.value)}
                        placeholder="Ingrese teléfono..."
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>

                  {/* Email del conductor */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        value={conductorData.email}
                        onChange={(e) => handleConductorDataChange('email', e.target.value)}
                        placeholder="Ingrese email..."
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>

                  {/* Tipo de Documento */}
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        Tipo de Documento <span className="text-danger">*</span>
                      </Form.Label>
                      <SearchableDropdown
                        options={tiposDocumento}
                        value={conductorData.tipo_documento_id}
                        onChange={(value) => handleConductorDataChange('tipo_documento_id', value)}
                        placeholder="Seleccionar tipo de documento..."
                        displayKey="documento"
                        valueKey="id"
                        searchKey="documento"
                        disabled={!isFieldEditable('conductor')}
                      />
                    </Form.Group>
                  </Col>
                </>
              ) : (
                /* Conductor existente */
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>
                      Conductor <span className="text-danger">*</span>
                      {isMissingField('conductor') && (
                        <small className="text-danger ms-1">(faltante)</small>
                      )}
                    </Form.Label>
                    <Select
                      value={selectedConductor}
                      onChange={handleConductorChange}
                      options={conductoresBasic}
                      placeholder="Buscar conductor por nombre o RUT..."
                      isClearable
                      isLoading={loadingConductores}
                      isSearchable
                      isDisabled={!isFieldEditable('conductor')}
                      noOptionsMessage={({ inputValue }) => 
                        inputValue.length < 2 
                          ? 'Escriba al menos 2 caracteres'
                          : 'No se encontraron conductores'
                      }
                      onInputChange={handleSearchChangeBasic}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: '38px'
                        })
                      }}
                    />
                  </Form.Group>
                </Col>
              )}

              {/* Empresa */}
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Empresa <span className="text-danger">*</span>
                    {isMissingField('empresa') && (
                      <small className="text-danger ms-1">(faltante)</small>
                    )}
                  </Form.Label>
                  <Select
                    value={selectedEmpresa}
                    onChange={handleEmpresaChange}
                    options={empresas.map(empresa => ({
                      value: empresa.id,
                      label: empresa.sigla,
                      data: empresa
                    }))}
                    placeholder="Seleccionar empresa..."
                    isClearable
                    isSearchable
                    isDisabled={!isFieldEditable('empresa')}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                </Form.Group>
              </Col>

              {/* Tipo de Flota */}
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Tipo de Flota
                    {isMissingField('tipo_flota') && (
                      <small className="text-danger ms-1">(faltante)</small>
                    )}
                  </Form.Label>
                  <Select
                    value={selectedTipoFlota}
                    onChange={handleTipoFlotaChange}
                    options={tiposFlota.map(tipo => ({
                      value: tipo.id,
                      label: tipo.tipo_flota,
                      data: tipo
                    }))}
                    placeholder="Seleccionar tipo de flota..."
                    isClearable
                    isSearchable
                    isDisabled={!isFieldEditable('tipo_flota')}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                </Form.Group>
              </Col>

              {/* Centro de Despacho */}
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Centro de Despacho
                    {isMissingField('centro_despacho') && (
                      <small className="text-danger ms-1">(faltante)</small>
                    )}
                  </Form.Label>
                  <Select
                    value={selectedCentroDespacho}
                    onChange={handleCentroDespachoChange}
                    options={centrosDespacho.map(centro => ({
                      value: centro.id,
                      label: centro.centro_despacho,
                      data: centro
                    }))}
                    placeholder="Seleccionar centro de despacho..."
                    isClearable
                    isSearchable
                    isDisabled={!isFieldEditable('centro_despacho')}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                </Form.Group>
              </Col>

              {/* TMS */}
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    TMS
                    {isMissingField('tms') && (
                      <small className="text-danger ms-1">(faltante)</small>
                    )}
                  </Form.Label>
                  <Select
                    value={selectedTMS}
                    onChange={handleTMSChange}
                    options={sistemasTMS.map(tms => ({
                      value: tms.id,
                      label: tms.tms,
                      data: tms
                    }))}
                    placeholder="Seleccionar TMS..."
                    isClearable
                    isSearchable
                    isDisabled={!isFieldEditable('tms')}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '38px'
                      })
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {usuarioSpread?.missing_fields && usuarioSpread.missing_fields.length > 0 && (
              <Alert variant="warning" className="mt-3">
                <strong>Campos faltantes:</strong> {usuarioSpread.missing_fields.join(', ')}
              </Alert>
            )}
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={isSubmitting || loading}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Actualizando...
            </>
          ) : (
            'Actualizar Usuario'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EditUsuarioSpreadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  usuarioSpread: PropTypes.object,
  conductores: PropTypes.array.isRequired,
  empresas: PropTypes.array.isRequired,
  tiposFlota: PropTypes.array.isRequired,
  centrosDespacho: PropTypes.array.isRequired,
  sistemasTMS: PropTypes.array.isRequired,
  tiposDocumento: PropTypes.array.isRequired,
  onAssign: PropTypes.func.isRequired,
  onCreateConductor: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default EditUsuarioSpreadModal;
